import React, { useState, useMemo } from 'react';
import {
  Typography,
  Button,
  Container,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  TableContainer,
  TableSortLabel,
  Grid
} from '@mui/material';
import {
  CheckCircle,
  Warning,
  Cancel,
  Schedule,
  Receipt,
  CreditCard,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useCurrentLocation } from 'src/components/Routes/LocationRoute';
import { useCancelSubscription, useDeleteLocation } from 'src/queries/locations';
import { initiatePayfastCheckout, getUpdatePaymentDetailsUrl } from '../../utils/payfast';
import SubscriptionAdvertisement from '../../components/SubscriptionAd';
import AlternativeSubscriptionAdvertisement from '../../components/AlternativeSubscriptionAd';
import DeleteMerchantConfirmationDialog from "src/components/DeleteMerchantConfirmationDialog";
import { useNavigate } from 'react-router-dom';
import { usePricing } from '../../hooks/usePricing';

type SortOrder = 'asc' | 'desc';

const SubscriptionPage: React.FC = () => {
  const { merchant } = useCurrentLocation();
  const [openPaymentConfirmationDialog, setOpenPaymentConfirmationDialog] =
    React.useState(false);
  const [cancelConfirmationDialog, setCancelConfirmationDialog] =
    React.useState(false);
  const [isReactivating, setIsReactivating] = React.useState(false);
  const [deleteConfirmationDialog, setDeleteConfirmationDialog] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const navigate = useNavigate();

  // The mount refetch is gone: this tab receives the record already loaded from
  // the route. It also used to gate on axios.defaults.headers.common.Authorization,
  // reading the token off a global as a readiness signal.

  const cancelSubscription = useCancelSubscription();
  const deleteLocation = useDeleteLocation();

  const handleCancelSubscription = () => {
    cancelSubscription.mutate({
      token: merchant.subscription!.token,
      locationId: merchant.id,
    });
  };

  // Hybrid model:
  //   - Paid merchants (countries with a CountryPricing row) stay on the paid
  //     PayFast flow (Activate / Update / Cancel / Renew). They can land here
  //     without a subscription if they bailed out of the registration step
  //     before paying — show the SubscriptionAd.
  //   - Free-plan merchants (no CountryPricing row for their country) have a
  //     subscription row with a `tmp-` token; show a simple Free Plan card
  //     with no actions.
  //   - Legacy paid merchants whose subscription has been rotated to a tmp-
  //     token after cancellation also fall into the Free Plan view.
  const { pricing } = usePricing(merchant.address?.countryCode);
  const isFreePlanMerchant = pricing?.is_free === true;
  const subStatus = merchant.subscription?.status?.toLowerCase();
  const hasFreePlanToken = !!merchant.subscription?.token?.startsWith('tmp-');
  const isFreePlanView =
    isFreePlanMerchant ||
    hasFreePlanToken ||
    (!!merchant.subscription && subStatus === 'cancelled');

  const handleUpdatePaymentDetails = async () => {
    try {
      const url = await getUpdatePaymentDetailsUrl(merchant.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Failed to fetch update-payment-details link:', error);
    }
  };

  const shouldUseAlternativeFlow = () => {
    if (!merchant.referralCode) {
      return false;
    }
    return merchant.referralCode?.trim().toLowerCase() === 'chris green';
  };

  // Sort payments by date
  const sortedPayments = useMemo(() => {
    if (!merchant.subscription?.payments) return [];

    return [...merchant.subscription.payments].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });
  }, [merchant.subscription?.payments, sortOrder]);

  const handleSortChange = () => {
    setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <CheckCircle color="success" />;
      case 'payment_overdue':
        return <Warning color="warning" />;
      case 'free_trial':
        return <Schedule color="info" />;
      default:
        return <Cancel color="error" />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'payment_overdue':
        return 'warning';
      case 'free_trial':
        return 'info';
      default:
        return 'error';
    }
  };

  const handlePaymentClickOpen = () => {
    setOpenPaymentConfirmationDialog(true);
  };

  const handlePaymentClickClose = () => {
    setOpenPaymentConfirmationDialog(false);
  };

  const handlePaymentRedirect = async () => {
    // The backend resolves the amount and (for reactivations) the merchant's
    // own subscription id — nothing price-related is sent from here.
    await initiatePayfastCheckout({
      merchantId: merchant.id,
      intent: isReactivating
        ? 'reactivate'
        : shouldUseAlternativeFlow()
        ? 'billImmediately'
        : 'subscribe',
      referralCode: merchant.referralCode || undefined
    });
  };

  const handleCancelSubscriptionClickOpen = () => {
    setCancelConfirmationDialog(true);
  };

  const handleCancelSubscriptionClickClose = () => {
    setCancelConfirmationDialog(false);
  };

  const handleDeleteMerchant = async () => {
    try {
      // Drops this location's cache entry and invalidates the list, so the
      // /locations page cannot show a location that no longer exists.
      await deleteLocation.mutateAsync(merchant.id);
      setDeleteConfirmationDialog(false);
      navigate('/locations');
    } catch (error) {
      console.error('Error deleting merchant:', error);
    }
  };

  if (cancelSubscription.isPending || deleteLocation.isPending) {
    return (
      <Container
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant='h3' sx={{ mb: 1, fontWeight: 600 }}>
          {merchant.name}'s Subscription
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Manage your subscription and billing information
        </Typography>
      </Box>

      {isFreePlanView ? (
        <Card>
          <CardContent sx={{ py: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <CheckCircle color="success" />
              <Typography variant='h5' sx={{ ml: 1, fontWeight: 600 }}>
                Free Plan
              </Typography>
            </Box>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 1 }}>
              You're on the free plan — no billing, no payment details required.
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Enjoy full access to BrewPerks. There's nothing to manage here.
            </Typography>
          </CardContent>
        </Card>
      ) : merchant.subscription ? (
        <Grid container spacing={3}>
          {/* Status Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {getStatusIcon(merchant.subscription.status)}
                  <Typography variant='h6' sx={{ ml: 1 }}>
                    Subscription Status
                  </Typography>
                </Box>
                <Chip
                  label={merchant.subscription.status.replace('_', ' ').toUpperCase()}
                  color={getStatusColor(merchant.subscription.status)}
                  size="medium"
                  sx={{ mb: 2, fontWeight: 600 }}
                />
                <Box sx={{ mt: 2 }}>
                  {merchant.subscription.status.toLowerCase() === 'active' ? (
                    <Box>
                      <Typography variant='body2' sx={{ mb: 2, color: 'success.main' }}>
                        ✓ Your subscription is active and will renew automatically
                      </Typography>
                      <Typography variant='body2' color="text.secondary">
                        <strong>Next billing date:</strong> {new Date(merchant.subscription.billingDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      </Typography>
                    </Box>
                  ) : merchant.subscription.status.toLowerCase() === 'free_trial' ? (
                    <Typography variant='body2' color="info.main">
                      🎉 Your free trial is active until {new Date(merchant.subscription.billingDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </Typography>
                  ) : merchant.subscription.status.toLowerCase() === 'payment_overdue' ? (
                    <Box>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant='body2'>
                          Your payment is overdue. Please update your payment details to continue using our services without interruption.
                        </Typography>
                      </Alert>
                      <Typography variant='body2' color="text.secondary">
                        <strong>Billing date:</strong> {new Date(merchant.subscription.billingDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant='body2' color="error.main">
                      Your subscription has been cancelled. Renew now to continue using our services.
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Actions Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CreditCard color="primary" />
                  <Typography variant='h6' sx={{ ml: 1 }}>
                    Account Actions
                  </Typography>
                </Box>
                <Stack spacing={2}>
                  {merchant.subscription.status.toLowerCase() === 'active' ||
                   merchant.subscription.status.toLowerCase() === 'free_trial' ||
                   merchant.subscription.status.toLowerCase() === 'payment_overdue' ? (
                    <>
                      <Button
                        variant='contained'
                        color='primary'
                        onClick={handleUpdatePaymentDetails}
                        startIcon={<CreditCard />}
                        size="large"
                        fullWidth
                      >
                        Update Payment Details
                      </Button>
                      <Button
                        variant='outlined'
                        color='error'
                        onClick={handleCancelSubscriptionClickOpen}
                        size="large"
                        fullWidth
                      >
                        Cancel Subscription
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant='contained'
                      color='primary'
                      onClick={() => {
                        handlePaymentClickOpen();
                        setIsReactivating(true);
                      }}
                      size="large"
                      fullWidth
                      startIcon={<CreditCard />}
                    >
                      Renew Subscription
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Payments Section */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Receipt color="primary" />
                  <Typography variant='h5' sx={{ ml: 1, fontWeight: 600 }}>
                    Payment History
                  </Typography>
                </Box>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>ID</strong></TableCell>
                        <TableCell><strong>Amount</strong></TableCell>
                        <TableCell><strong>Payfast Payment ID</strong></TableCell>
                        <TableCell><strong>Invoice</strong></TableCell>
                        <TableCell>
                          <TableSortLabel
                            active={true}
                            direction={sortOrder}
                            onClick={handleSortChange}
                            sx={{ fontWeight: 'bold' }}
                          >
                            Date
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedPayments.map((payment) => (
                        <TableRow key={payment.id} hover>
                          <TableCell>{payment.id}</TableCell>
                          <TableCell>
                            <Chip
                              label={`R${payment.amount}`}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {payment.pfPaymentId}
                          </TableCell>
                          <TableCell>
                            <Button
                              href={payment.invoiceUrl}
                              target='_blank'
                              rel='noreferrer'
                              variant="text"
                              size="small"
                              startIcon={<Receipt />}
                            >
                              View Invoice
                            </Button>
                          </TableCell>
                          <TableCell>
                            {new Date(payment.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                        </TableRow>
                      ))}
                      {sortedPayments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="text.secondary">
                              No payment history available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ) : (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Start Your Subscription
            </Typography>
            {shouldUseAlternativeFlow() ? (
              <AlternativeSubscriptionAdvertisement
                onProceed={handlePaymentClickOpen}
                countryCode={merchant.address?.countryCode}
                typeName={merchant.type?.name}
              />
            ) : (
              <SubscriptionAdvertisement
                onProceed={handlePaymentClickOpen}
                referralCode={merchant.referralCode}
                countryCode={merchant.address?.countryCode}
                typeName={merchant.type?.name}
              />
            )}
          </CardContent>
        </Card>
      )}

      {/* Danger Zone */}
      <Card sx={{ mt: 4, borderColor: 'error.main', borderWidth: 1, borderStyle: 'solid' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <DeleteIcon color="error" />
            <Typography variant="h6" color="error" sx={{ ml: 1 }}>
              Danger Zone
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Permanently delete this location and all associated data. This action cannot be undone.
          </Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setDeleteConfirmationDialog(true)}
            startIcon={<DeleteIcon />}
          >
            Delete Location
          </Button>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <Dialog
        open={openPaymentConfirmationDialog}
        onClose={handlePaymentClickClose}
      >
        <DialogTitle>Payment Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to be redirected to our secure payment provider's
            portal. Do you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaymentClickClose} color='primary'>
            Cancel
          </Button>
          <Button onClick={handlePaymentRedirect} color='primary'>
            Proceed to Payment
          </Button>
        </DialogActions>

        {merchant.address?.countryCode && merchant.address.countryCode !== 'ZA' && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', fontSize: '0.75rem', lineHeight: 1.5 }}
            >
              Note: Subscription prices are fixed in South African Rand (ZAR). Monthly billing amounts may
              fluctuate with exchange rates. BrewPrks performs yearly exchange rate adjustments.
            </Typography>
          </Box>
        )}
      </Dialog>

      <Dialog
        open={cancelConfirmationDialog}
        onClose={handleCancelSubscriptionClickClose}
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelSubscriptionClickClose}
            color='primary'
          >
            No
          </Button>
          <Button onClick={handleCancelSubscription} color='primary'>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteMerchantConfirmationDialog
        isOpen={deleteConfirmationDialog}
        merchantName={merchant.name}
        handleClose={() => setDeleteConfirmationDialog(false)}
        handleConfirm={handleDeleteMerchant}
      />
    </Container>
  );
};

export default SubscriptionPage;
